import React, { useState } from 'react';

interface EmailPopupProps {
  onSubmit: (email: string) => Promise<void>;
  title?: string;
  description?: string;
  reportName?: string;
  privacyNote?: string;
  buttonText?: string;
  loadingText?: string;
}

/**
 * A reusable email collection popup component for reports
 * Can be customized with different text for different reports
 */
const EmailPopup: React.FC<EmailPopupProps> = ({ 
  onSubmit,
  title = "Get Access to the Full Report",
  description = "Enter your email to view the complete report.",
  reportName = "",
  privacyNote = "We respect your privacy. Your email will only be used to send you updates about event marketing trends.",
  buttonText = "Access Report",
  loadingText = "Submitting..."
}) => {
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      setIsValid(false);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Call the onSubmit handler and wait for it to complete
      await onSubmit(email);
      
      // No need to reset isSubmitting as the component will unmount if successful
    } catch (error) {
      // If there's an error, stop the loading state
      setIsSubmitting(false);
      // Error is handled in the parent component
    }
  };

  return (
    <div className="email-popup-overlay">
      <div className="email-popup">
        <div className="email-popup-content">
          <h2>{title}</h2>
          <p>{description} {reportName && <strong>{reportName}</strong>}</p>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setIsValid(true);
                }}
                placeholder="Your email address"
                className={`email-input ${!isValid ? 'error' : ''}`}
                required
              />
              {!isValid && <div className="error-message">Please enter a valid email address</div>}
            </div>
            
            <button 
              type="submit" 
              className="print-btn email-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? loadingText : buttonText}
            </button>
          </form>
          
          <div className="privacy-note">
            {privacyNote}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPopup;